import {
  IActionType,
  VideoState,
  KAny,
  VideoRiskTS,
  RiskLabelTree,
} from "../../types";
import { consoleInfo, consoleError } from "../../utils/logger";
import axios from "axios";
// import * as d3Color from "d3-scale-chromatic";

const initialState: VideoState = {
  videoId: null,
  videoSrc: null,
  videoSize: [],
  videoFPS: 30,
  videoAnnotations: [],
  videoRiskTS: {},
  videoASRText: [],
  videoFrames: [],
  riskColorMap: new Map(),
};

export default (state = initialState, action: IActionType): VideoState => {
  switch (action.type) {
    case "INIT_SERVER": {
      const { msg } = action.payload;
      axios
        .get(`/api/hello?message=${msg}`)
        .then((res) => {
          consoleInfo(res.data.express);
        })
        .catch((e) => {
          consoleError(e);
        });
      return state;
    }
    case "FETCH_DEMOVIDEO": {
      return action.payload;
    }
    case "FETCH_YOUKUVIDEO": {
      const {
        imageSrc,
        imageAlgJson,
        videoSrc,
        videoId,
        videoAlgJson,
      } = action.payload;
      const videoRiskTS = getVideoRiskTS(videoAlgJson);
      const riskLabelTree = getRiskLabelTree(videoRiskTS);
      const riskColorMap = getRiskColorMap(riskLabelTree);
      return {
        videoId: videoId,
        videoSrc: videoSrc,
        videoSize: [],
        videoFPS: 30,
        videoAnnotations: [],
        videoRiskTS: videoRiskTS,
        videoASRText: getVideoASRText(videoAlgJson),
        videoFrames: imageAlgJson.data,
        riskColorMap: riskColorMap,
      };
    }
    case "FETCH_STUDYVIDEO": {
      const {
        imageSrc,
        imageAlgJson,
        videoSrc,
        videoId,
        videoAlgJson,
        audioAlgJson,
      } = action.payload;
      const videoRiskTS = getVideoRiskTS(videoAlgJson);
      const riskLabelTree = getRiskLabelTree(videoRiskTS);
      const riskColorMap = getRiskColorMap(riskLabelTree);
      return {
        videoId: videoId,
        videoSrc: videoSrc,
        videoSize: [],
        videoFPS: 30,
        videoAnnotations: [],
        videoRiskTS: videoRiskTS,
        videoASRText: getVideoASRText(videoAlgJson),
        audioScript: audioAlgJson,
        videoFrames: imageAlgJson.data,
        riskColorMap: riskColorMap,
      };
    }
    default:
      return state;
  }
};

function getVideoRiskTS(videoAlgJson: KAny) {
  const videoRisk = {};
  for (const label in videoAlgJson) {
    if (isTSKV(videoAlgJson[label])) {
      videoRisk[label] = videoAlgJson[label];
    }
  }
  return videoRisk;
}

function isTSKV(TS) {
  if (TS instanceof Object) {
    return typeof Object.values(TS)[0] === "number";
  }
  return false;
}

function getVideoASRText(videoAlgJson: KAny) {
  let videoASRText = [];
  if (videoAlgJson.hasOwnProperty("ASR文本敏感关键词")) {
    const ASRText = videoAlgJson["ASR文本敏感关键词"];
    const timeTicks = Object.keys(ASRText).map((timeStr) => +timeStr);
    timeTicks.sort((a, b) => a - b);
    let i = 0;
    for (let len = timeTicks.length - 1; i < len; i++) {
      const _startTime = timeTicks[i];
      const _endTime = timeTicks[i + 1];
      const _text = ASRText[`${_startTime}`];
      videoASRText.push({
        start: _startTime,
        end: _endTime,
        text: _text,
      });
    }
    const lastTime = timeTicks[i];
    if (ASRText[`${lastTime}`].length > 0) {
      const newEndTime = 2 * timeTicks[i] - timeTicks[i - 1];
      videoASRText.push({
        start: lastTime,
        end: newEndTime,
        text: ASRText[`${lastTime}`],
      });
    }
    return videoASRText;
  }
  if (videoAlgJson.hasOwnProperty("ASR文本")) {
    videoASRText = videoAlgJson["ASR文本"];
  }
  return videoASRText;
}

function getRiskLabelTree(videoRiskTS: VideoRiskTS) {
  const rawRiskLabels = Object.keys(videoRiskTS);
  const riskLabelTree = {};
  for (let i = 0, len = rawRiskLabels.length; i < len; i++) {
    const riskLabel = rawRiskLabels[i];
    const riskValue = videoRiskTS[riskLabel];
    if (aggregateArr(Object.values(riskValue)) > 0) {
      riskDataProtocV1(riskLabelTree, riskLabel, riskValue);
    }
  }
  return riskLabelTree;
}

function aggregateArr(arr) {
  const reducer = (accumulator, currentValue) => accumulator + currentValue;
  return arr.reduce(reducer);
}

// 采用风险数据协议v1
function riskDataProtocV1(riskLabelTree, riskLabel, riskValue) {
  let riskLabelTags = riskLabel
    .split("_")
    .filter((tag) => tag !== "Det" && tag !== "v0");
  if (riskLabelTags[1] === "情趣品") {
    riskLabelTags = [aggregateArr(riskLabelTags)];
  }
  switch (riskLabelTags.length) {
    case 1:
      if (!riskLabelTree.hasOwnProperty("低俗色情")) {
        riskLabelTree["低俗色情"] = {};
      }
      riskLabelTree["低俗色情"][riskLabelTags[0]] = riskValue;
      break;
    case 2:
      if (!riskLabelTree.hasOwnProperty(riskLabelTags[0])) {
        riskLabelTree[riskLabelTags[0]] = {};
      }
      riskLabelTree[riskLabelTags[0]][riskLabelTags[1]] = riskValue;
      break;
    default:
      break;
  }
}

const colorScheme = [
  {
    risk: "FA",
    policy: "Flase Advertising",
    color: "#fb8072",
  },
  {
    risk: "PP",
    policy: "Protected Products",
    color: "#80b1d3",
  },
  {
    risk: "IB",
    policy: "Inappropriate Business",
    color: "#fdb462",
  },
  {
    risk: "SC",
    policy: "Sensitive content",
    color: "#8dd3c7",
  },
  {
    risk: "NM",
    policy: "Normal",
    color: "white",
  },
];

function getRiskColorMap(riskLabelTree: RiskLabelTree) {
  // const riskCategories = Object.keys(riskLabelTree);
  // const riskColorMap = new Map();
  // riskCategories.forEach((riskType, idx) => {
  //   riskColorMap.set(riskType, colorScheme[idx]);
  // });
  const riskColorMap = new Map();
  colorScheme.forEach((item) => {
    riskColorMap.set(item.risk, item.color);
  });
  return riskColorMap;
}
